/**
 * OneClick V2 类型定义
 *
 * EIP-712 签名相关的枚举、常量和 Action 定义。
 * 注意：API 请求/响应类型由 openapi-generator 自动生成，
 * 位于 @ui/services/backend/oneClickServiceGenerated/models
 */

import type { Hex } from 'viem';

// ═══════════════════ OneClickAction 枚举 ═══════════════════

export const OneClickAction = {
  MARKET_OPEN: 0,
  MARKET_CLOSE: 1,
  LIMIT_OPEN: 2,
  LIMIT_CANCEL: 3,
  LIMIT_UPDATE_TP_SL: 4,
  ADD_MARGIN: 5,
  REMOVE_MARGIN: 6,
  UPDATE_TP_SL: 7,
  BATCH_MARKET_CLOSE: 8,
  PARTIAL_CLOSE: 9,
  CANCEL_DECREASE_ORDER: 10,
  BATCH_CREATE_DECREASE_ORDERS: 11,
  BATCH_UPDATE_DECREASE_ORDERS: 12,
} as const;

export type OneClickActionValue =
  (typeof OneClickAction)[keyof typeof OneClickAction];

// ═══════════════════ DecreaseOrder (for BATCH_CREATE_DECREASE_ORDERS) ═══════════════════

export const DecreaseOrderKind = {
  TP: 0,
  SL: 1,
} as const;

/** [kind, triggerPrice, closeQty, broker] */
export type DecreaseOrderInput = readonly [
  kind: number,
  triggerPrice: bigint,
  closeQty: bigint,
  broker: number,
];

/** [orderHash, triggerPrice, closeQty, broker] */
export type DecreaseOrderUpdateInput = readonly [
  orderHash: Hex,
  triggerPrice: bigint,
  closeQty: bigint,
  broker: number,
];

// ═══════════════════ EIP-712 Domain ═══════════════════

export const EIP712_DOMAIN_NAME = 'LeverupOneClickV2';
export const EIP712_DOMAIN_VERSION = '1';

// ═══════════════════ EIP-712 通用字段 (unified actionDataHash) ═══════════════════

export const COMMON_EIP712_FIELDS = [
  { name: 'trader', type: 'address' },
  { name: 'action', type: 'uint8' },
  { name: 'nonce', type: 'uint64' },
  { name: 'deadline', type: 'uint48' },
  { name: 'feeToken', type: 'address' },
  { name: 'antiDdosFee', type: 'uint96' },
  { name: 'actionDataHash', type: 'bytes32' },
] as const;

// ═══════════════════ Action EIP-712 Type 名称 ═══════════════════

/**
 * 各 action 对应的 EIP-712 type 名称。
 * 所有 action 的签名结构统一为 COMMON_EIP712_FIELDS（含 actionDataHash）。
 */
export const ACTION_TYPE_NAMES: Record<number, string> = {
  [OneClickAction.MARKET_OPEN]: 'OneClickMarketOpen',
  [OneClickAction.MARKET_CLOSE]: 'OneClickMarketClose',
  [OneClickAction.LIMIT_OPEN]: 'OneClickLimitOpen',
  [OneClickAction.LIMIT_CANCEL]: 'OneClickLimitCancel',
  [OneClickAction.LIMIT_UPDATE_TP_SL]: 'OneClickLimitUpdateTpSl',
  [OneClickAction.ADD_MARGIN]: 'OneClickAddMargin',
  [OneClickAction.REMOVE_MARGIN]: 'OneClickRemoveMargin',
  [OneClickAction.UPDATE_TP_SL]: 'OneClickUpdateTpSl',
  [OneClickAction.BATCH_MARKET_CLOSE]: 'OneClickBatchMarketClose',
  [OneClickAction.PARTIAL_CLOSE]: 'OneClickPartialClose',
  [OneClickAction.CANCEL_DECREASE_ORDER]: 'OneClickCancelDecreaseOrder',
  [OneClickAction.BATCH_CREATE_DECREASE_ORDERS]:
    'OneClickBatchCreateDecreaseOrders',
  [OneClickAction.BATCH_UPDATE_DECREASE_ORDERS]:
    'OneClickBatchUpdateDecreaseOrders',
};

// ═══════════════════ Action Data ABI 类型 (actionData 编码用) ═══════════════════

/**
 * 各 action 的 actionData ABI 参数类型（不含 trader）。
 *
 * actionData = abi.encode(trader, ...actionParams)[32:]
 * 编码时会在前面加上 trader address，然后剥离前 32 字节。
 *
 * @see 文档 §4 actionData 编码规则
 */
export const ACTION_DATA_ABI_TYPES: Record<number, readonly string[]> = {
  [OneClickAction.MARKET_OPEN]: [
    'address',
    'bool',
    'address',
    'address',
    'uint96',
    'uint128',
    'uint128',
    'uint128',
    'uint128',
    'uint24',
    'uint96',
  ],
  [OneClickAction.MARKET_CLOSE]: ['bytes32', 'uint24'],
  [OneClickAction.LIMIT_OPEN]: [
    'address',
    'bool',
    'address',
    'address',
    'uint96',
    'uint128',
    'uint128',
    'uint128',
    'uint128',
    'uint24',
    'uint96',
  ],
  [OneClickAction.LIMIT_CANCEL]: ['bytes32'],
  [OneClickAction.LIMIT_UPDATE_TP_SL]: ['bytes32', 'uint128', 'uint128'],
  [OneClickAction.ADD_MARGIN]: ['bytes32', 'address', 'uint96'],
  [OneClickAction.REMOVE_MARGIN]: ['bytes32', 'uint96'],
  [OneClickAction.UPDATE_TP_SL]: ['bytes32', 'uint128', 'uint128'],
  [OneClickAction.BATCH_MARKET_CLOSE]: ['bytes32[]', 'uint24'],
  [OneClickAction.PARTIAL_CLOSE]: ['bytes32', 'uint128', 'uint24'],
  [OneClickAction.CANCEL_DECREASE_ORDER]: ['bytes32'],
  [OneClickAction.BATCH_CREATE_DECREASE_ORDERS]: [
    'bytes32',
    '(uint8,uint128,uint128,uint24)[]',
  ],
  [OneClickAction.BATCH_UPDATE_DECREASE_ORDERS]: [
    '(bytes32,uint128,uint128,uint24)[]',
  ],
};
